import { useState } from 'react';
import { useNotificationProvider } from 'hooks'; // Adjust this path to your notification provider hook
import { CANtrackComment } from '../types/comment';
import { updateComment } from 'api/comments/updateComment';
import { useLoading } from 'contextProviders/LoadingBackdropProvider';
import { createComment } from 'api/comments/createComment';

const useSaveComment = (
	setComments: React.Dispatch<React.SetStateAction<CANtrackComment[]>>,
) => {
	const [isSaving, setIsSaving] = useState(false);
	const { setIsLoading } = useLoading();
	const { notifyError, notifySuccess } = useNotificationProvider();

	/**
	 * Saves the updated comment if canSave is true.
	 * Updates the comment in the state and notifies the user of success or failure.
	 *
	 * @param {CANtrackComment} comment - The comment object with updated values.
	 * @param {() => void} callback - The callback function to execute after the save operation is complete.
	 * @returns {Promise<void>} - A promise that resolves when the save operation is complete.
	 */
	const saveComment = async (
		comment: CANtrackComment,
		callback?: () => void,
	) => {
		setIsSaving(true);
		setIsLoading(true);
		try {
			if (comment.id === 0) {
				comment.id = await createComment(comment);
				setComments((prevComments) => [comment, ...prevComments]);
			} else {
				await updateComment(comment);
				setComments((prevComments) =>
					prevComments.map((c) => {
						if (c.id === comment.id) {
							return comment;
						}
						return c;
					}),
				);
			}
			if (callback) {
				callback();
			}
			notifySuccess('Entry updated successfully');
		} catch {
			notifyError('Error updating entry');
		} finally {
			setIsSaving(false);
			setIsLoading(false);
		}
	};

	return {
		isSaving,
		saveComment,
	};
};

export default useSaveComment;
